import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import apiService from '../services/apiService';

export default function Inventory() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ categories: [], statuses: [] });
  const [q, setQ] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: '',
    sku: '',
    category: 'skip',
    serial_number: '',
    description: '',
    status: 'active',
  });

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = items;
    if (categoryFilter) {
      list = list.filter((it) => it.category === categoryFilter);
    }
    if (!s) return list;
    return list.filter((it) => {
      const a = String(it.name || '').toLowerCase();
      const b = String(it.sku || '').toLowerCase();
      const c = String(it.serial_number || '').toLowerCase();
      return a.includes(s) || b.includes(s) || c.includes(s);
    });
  }, [items, q, categoryFilter]);

  async function fetchAll() {
    setError('');
    setLoading(true);
    try {
      const [list, m] = await Promise.all([
        apiService.get('inventory-items?include_inactive=1'),
        apiService.get('inventory-items/meta'),
      ]);
      setItems(Array.isArray(list) ? list : []);
      setMeta(m || { categories: [], statuses: [] });
    } catch (e) {
      setError(e.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCreate() {
    setError('');
    setBusy(true);
    try {
      await apiService.post('inventory-items', {
        name: form.name,
        sku: form.sku || null,
        category: form.category,
        serial_number: form.serial_number || null,
        description: form.description || null,
        status: form.status,
      });
      setOpen(false);
      setForm({
        name: '',
        sku: '',
        category: 'skip',
        serial_number: '',
        description: '',
        status: 'active',
      });
      await fetchAll();
    } catch (e) {
      setError(e.message || 'Failed to create item');
    } finally {
      setBusy(false);
    }
  }

  async function handleRetire(id) {
    if (!window.confirm('Mark this asset as retired?')) return;
    setBusy(true);
    try {
      await apiService.put(`inventory-items/${id}`, { status: 'retired' });
      await fetchAll();
    } catch (e) {
      setError(e.message || 'Failed to update item');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this inventory item permanently?')) return;
    setBusy(true);
    try {
      await apiService.delete(`inventory-items/${id}`);
      await fetchAll();
    } catch (e) {
      setError(e.message || 'Failed to delete item');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Inventory
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Skips, vehicles, tools, and equipment. Assign assets to jobs from the job detail panel.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 200, flex: '1 1 200px' }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="cat-filter-label">Category</InputLabel>
          <Select
            labelId="cat-filter-label"
            label="Category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            {(meta.categories || []).map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          Add asset
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {filtered.length === 0 ? (
            <Typography color="text.secondary">No inventory items yet.</Typography>
          ) : (
            filtered.map((it) => (
              <Card key={it.id} variant="outlined">
                <CardContent sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {it.name}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      <Chip size="small" label={it.category} />
                      {it.sku ? <Chip size="small" variant="outlined" label={`SKU ${it.sku}`} /> : null}
                      {it.serial_number ? <Chip size="small" variant="outlined" label={it.serial_number} /> : null}
                      <Chip
                        size="small"
                        color={it.status === 'active' ? 'success' : 'default'}
                        label={it.status}
                      />
                    </Box>
                    {it.description ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {it.description}
                      </Typography>
                    ) : null}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    {it.status === 'active' ? (
                      <Button size="small" onClick={() => handleRetire(it.id)} disabled={busy}>
                        Retire
                      </Button>
                    ) : null}
                    <IconButton size="small" aria-label="Delete" onClick={() => handleDelete(it.id)} disabled={busy}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add inventory asset</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <FormControl>
            <InputLabel id="new-cat-label">Category</InputLabel>
            <Select
              labelId="new-cat-label"
              label="Category"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            >
              {(meta.categories || ['skip', 'vehicle', 'tool', 'equipment', 'other']).map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="SKU" value={form.sku} onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} />
          <TextField
            label="Serial / ID"
            value={form.serial_number}
            onChange={(e) => setForm((f) => ({ ...f, serial_number: e.target.value }))}
          />
          <TextField
            label="Description"
            multiline
            minRows={2}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={busy || !form.name.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
